import { collectionFilterParams } from '../helpers/URLParams';
import { canDownloadPageItem } from '../helpers/PageSections';
import { APIsearchPageFiles, APIsearchPageCollections } from '../api/Pages';
import {
  addSelectedFiles,
  clearFilesList,
  getAvailableFilters,
  getFiles,
  setFilesEnd,
  setFilesLoading,
  setFilesParams,
  setFilesSearchType,
  setFilesTotal,
  setSelectedAllFiles,
  setSelectedFilters,
} from './Files';
import {
  addSelectedCollections,
  clearCollectionsList,
  getCollections,
  setCollectionsEnd,
  setCollectionsLoading,
  setCollectionsParams,
  setSelectedAllCollections,
} from './Collections';

export const SET_SEARCH_TERM = 'SET_SEARCH_TERM';
export const SET_SEARCH_QUERY_TYPE = 'SET_SEARCH_QUERY_TYPE';
export const SET_SEARCH_FIELDS = 'SET_SEARCH_FIELDS';
export const CLEAR_SEARCH_FIELDS = 'CLEAR_SEARCH_FIELDS';

export const setSearchTerm = search => ({
  type: SET_SEARCH_TERM,
  search,
});

export const setSearchQueryType = searchQueryType => ({
  type: SET_SEARCH_QUERY_TYPE,
  searchQueryType,
});

export const setSearchFields = searchFields => ({
  type: SET_SEARCH_FIELDS,
  searchFields,
});

export const clearSearchFields = () => ({
  type: CLEAR_SEARCH_FIELDS,
});

const externalSearchEndpoints = {
  'custom-page': {
    files: APIsearchPageFiles,
    collections: APIsearchPageCollections,
  },
};

const parseSearchResponse = ({ data }) => {
  const results = Array.isArray(data.data) ? data : data.data;
  const meta = data.meta || results.meta;
  return {
    items: results.data,
    filters: results.filters || data.filters,
    meta,
    end: meta.has_more !== undefined
      ? !meta.has_more
      : meta.current_page >= meta.last_page,
  };
};

const buildRequestParams = (state, params, filters = state.Files.filters) => {
  const { search, query_type: queryType, search_fields: searchFields } = state.Search;
  return {
    ...params,
    ...filters,
    ...(state.Files.collectionUuid && { collectionUuid: state.Files.collectionUuid }),
    ...(search && { search }),
    ...(queryType && { query_type: queryType }),
    ...(searchFields?.length > 0 && { search_fields: searchFields }),
  };
};

export const fetchExternalFiles = (entityType, entityId) => (dispatch, getState) => {
  const searchFiles = externalSearchEndpoints[entityType]?.files;
  const state = getState();
  if (!searchFiles || state.Files.end || state.Files.loading) {
    return Promise.resolve(null);
  }

  const params = { ...state.Files.params };
  params.per_page = params.perPage || params.per_page || 36;
  delete params.perPage;
  params.page = params.page || 0;

  dispatch(setFilesLoading(true));

  if ((!params.noFilters || params.noFilters === 'false') && params.page === 0) {
    searchFiles(entityId, buildRequestParams(state, { ...params, only_filters: true }))
      .then(({ data }) => {
        const filters = data.data?.filters || data.filters;
        if (!filters) return;
        dispatch(getAvailableFilters(filters));
        if (filters.selected_filters?.length > 0) {
          dispatch(setFilesSearchType('latest'));
        }
      })
      .catch(error => error);
  }

  params.page += 1;

  return searchFiles(entityId, buildRequestParams(state, params))
    .then((response) => {
      const {
        items, filters, meta, end,
      } = parseSearchResponse(response);
      dispatch(getFiles(items));
      if (getState().Files.selectedAllFiles) {
        dispatch(addSelectedFiles(items.filter(canDownloadPageItem)));
        if (end) dispatch(setSelectedAllFiles(false));
      }
      if (Array.isArray(filters?.selected_filters)) {
        dispatch(setSelectedFilters(filters.selected_filters));
      }
      dispatch(setFilesLoading(false));
      dispatch(setFilesEnd(end));
      dispatch(setFilesParams({ page: meta.current_page || params.page }));
      dispatch(setFilesTotal(meta.total));
      return response.data;
    })
    .catch((error) => {
      if (!window.axios.isCancel(error)) dispatch(setFilesLoading(false));
      return error;
    });
};

export const fetchExternalFilters = (
  entityType,
  entityId,
  requestParams = {},
) => (dispatch, getState) => {
  const searchFiles = externalSearchEndpoints[entityType]?.files;
  if (!searchFiles) return Promise.resolve(null);

  const params = {
    page: 0,
    per_page: 1,
    orderby: getState().Files.params.orderby,
    order: getState().Files.params.order,
    only_filters: true,
  };

  return searchFiles(entityId, { ...buildRequestParams(getState(), params), ...requestParams })
    .then(({ data }) => {
      const filters = data.data?.filters || data.filters;
      if (filters) dispatch(getAvailableFilters(filters));
    })
    .catch(error => error);
};

export const fetchExternalCollections = (entityType, entityId) => (dispatch, getState) => {
  const searchCollections = externalSearchEndpoints[entityType]?.collections;
  const state = getState();
  if (!searchCollections ||
    state.Collections.collectionsEnd ||
    state.Collections.collectionsLoading) {
    return Promise.resolve(null);
  }
  const hasValue = v => (Array.isArray(v) ? v.length > 0 : !!v);
  const { search, search_fields: searchFields } = state.Search;
  // Filters collections don't support
  const hasFileOnlyFilters = Object.entries(state.Files.filters || {}).some(([key, value]) => (
    !collectionFilterParams.includes(key) && hasValue(value)
  ));
  const hasCollectionCriteria =
    !!search || searchFields?.length > 0 || !!state.Files.collectionUuid ||
    Object.values(state.Collections.filters || {}).some(hasValue);
  // Only file filters are active
  if (hasFileOnlyFilters && !hasCollectionCriteria) {
    dispatch(clearCollectionsList());
    dispatch(setCollectionsEnd(true));
    return Promise.resolve(null);
  }

  const params = { ...state.Collections.params };
  params.per_page = params.perPage || params.per_page || 36;
  delete params.perPage;
  params.page = (params.page || 0) + 1;
  const { orderby, order } = state.Files.params;
  if (orderby) params.orderby = orderby;
  if (order) params.order = order;

  dispatch(setCollectionsLoading(true));

  return searchCollections(entityId, buildRequestParams(state, params, state.Collections.filters))
    .then((response) => {
      const { items, meta, end } = parseSearchResponse(response);
      dispatch(getCollections(items, meta.total));
      if (getState().Collections.selectedAllCollections) {
        dispatch(addSelectedCollections(items.filter(canDownloadPageItem)));
        if (end) dispatch(setSelectedAllCollections(false));
      }
      dispatch(setCollectionsLoading(false));
      dispatch(setCollectionsEnd(end));
      dispatch(setCollectionsParams({ page: meta.current_page || params.page }));
      return response.data;
    })
    .catch((error) => {
      if (!window.axios.isCancel(error)) dispatch(setCollectionsLoading(false));
      return error;
    });
};

export const refreshExternalSearch = (entityType, entityId) => (dispatch) => {
  dispatch(clearFilesList());
  dispatch(setFilesParams({ page: 0 }));
  dispatch(setFilesEnd(false));
  dispatch(setFilesLoading(false));

  dispatch(clearCollectionsList());
  dispatch(setCollectionsParams({ page: 0 }));
  dispatch(setCollectionsEnd(false));
  dispatch(setCollectionsLoading(false));

  return Promise.all([
    dispatch(fetchExternalFiles(entityType, entityId)),
    dispatch(fetchExternalCollections(entityType, entityId)),
  ]);
};
